import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 여러 화면을 거쳐 도착한 결과 화면에서, 뒤로가기를 흐름 진입 전 화면으로 되돌린다.
 * 목적지 화면이 markBackTarget 으로 히스토리 위치를 남겨두면 그 지점까지 한 번에 걷어낸다.
 *
 * 안드로이드 하드웨어 뒤로가기는 웹뷰의 history back 이라 popstate 로 잡는다.
 * 감시용 히스토리를 한 칸 얹어두고, 그게 pop 되는 순간을 뒤로가기로 본다.
 */

const KEY_PREFIX = 'backTo:';

/** 뒤로가기 목적지가 되는 화면에서 호출. */
export const markBackTarget = (path) => {
    const idx = window.history.state?.idx;
    if (Number.isInteger(idx)) {
        sessionStorage.setItem(KEY_PREFIX + path, String(idx));
    }
};

const readTargetIdx = (path) => {
    const raw = sessionStorage.getItem(KEY_PREFIX + path);
    if (raw === null) return null;
    const idx = Number(raw);
    return Number.isInteger(idx) ? idx : null;
};

const useBackTo = (path) => {
    const navigate = useNavigate();
    const doneRef = useRef(false);

    const goBack = useCallback(() => {
        // go(-n) 이 끝날 때 popstate 가 한 번 더 오는데, 그땐 이미 목적지다.
        if (doneRef.current) return;
        doneRef.current = true;

        const from = window.history.state?.idx;
        const to = readTargetIdx(path);

        if (Number.isInteger(from) && to !== null && from > to) {
            navigate(to - from);
        } else {
            navigate(path, { replace: true });
        }
    }, [path, navigate]);

    useEffect(() => {
        const state = window.history.state;
        window.history.pushState(
            { ...state, idx: (state?.idx ?? 0) + 1 },
            '',
        );
    }, []);

    useEffect(() => {
        window.addEventListener('popstate', goBack);
        return () => window.removeEventListener('popstate', goBack);
    }, [goBack]);

    return goBack;
};

export default useBackTo;
